import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import PDFViewer from './components/PDFViewer';
import FieldRulesPanel from './components/FieldRulesPanel';
import DocumentSearch from './components/DocumentSearch';
import ConfigDownload from './components/ConfigDownload';
import PageNavigation from './components/PageNavigation';
import ToastContainer from './components/ToastContainer';
import ApiKeyPrompt from './components/ApiKeyPrompt';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_KEY_STORAGE_KEY = 'validation_tool_api_key';

function App() {
  // API Key State
  const [apiKey, setApiKey] = useState(null);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);

  // Existing state
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedDocVersion, setSelectedDocVersion] = useState(null);
  const [fields, setFields] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedField, setSelectedField] = useState(null);
  const [rules, setRules] = useState({
    required_fields: {},
    conditional_rules: [],
    unconditional_rules: [],
    custom_requirements: []
  });
  const [toasts, setToasts] = useState([]);
  const [conditionalRuleMode, setConditionalRuleMode] = useState(null);
  const [unconditionalRuleMode, setUnconditionalRuleMode] = useState(null);

  // Configure axios with API key (only once)
  const configureAxios = React.useCallback((key) => {
    // Set default header for all axios requests
    axios.defaults.headers.common['X-API-Key'] = key;

    // Add response interceptor to handle 401 (only add once)
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // API key invalid - clear and prompt again
          localStorage.removeItem(API_KEY_STORAGE_KEY);
          setApiKey(null);
          setShowApiKeyPrompt(true);
          showToast('Invalid API key. Please enter a valid key.', 'error');
        }
        return Promise.reject(error);
      }
    );

    // Return cleanup function to remove interceptor
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Check for stored API key on mount
  useEffect(() => {
    const storedKey = localStorage.getItem(API_KEY_STORAGE_KEY);
    if (storedKey) {
      setApiKey(storedKey);
      const cleanup = configureAxios(storedKey);
      return cleanup;
    } else {
      setShowApiKeyPrompt(true);
    }
  }, [configureAxios]);

  // Load document types only after API key is set
  useEffect(() => {
    if (apiKey && !showApiKeyPrompt) {
      loadDocumentTypes();
    }
  }, [apiKey, showApiKeyPrompt]);

  // Handle API key submission
  const handleApiKeySubmit = (key) => {
    localStorage.setItem(API_KEY_STORAGE_KEY, key);
    setApiKey(key);
    setShowApiKeyPrompt(false);
    configureAxios(key);
    showToast('API key saved successfully', 'success');
  };

  useEffect(() => {
    if (selectedDocType) {
      // Clear fields when switching documents/versions to avoid showing wrong coordinates
      setFields([]);
      // Only load if we have a document type (version is optional)
      if (selectedDocVersion !== undefined) {
        loadFields();
        loadPages();
        loadRules();
      }
    }
  }, [selectedDocType, selectedDocVersion]);

  const loadDocumentTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE}/documents/types`);
      setDocumentTypes(response.data.document_types);
      // Don't auto-select any document - user must select via search
    } catch (error) {
      console.error('Error loading document types:', error);
    }
  };

  const loadFields = async () => {
    if (!selectedDocType) {
      console.warn('Cannot load fields: missing docType', { selectedDocType });
      return;
    }
    try {
      const params = new URLSearchParams();
      // Only add version if it exists (version is optional)
      if (selectedDocVersion) {
        params.append('document_version_type', selectedDocVersion);
      }
      const url = `${API_BASE}/fields/${selectedDocType}/all${params.toString() ? '?' + params.toString() : ''}`;
      console.log('Loading fields for:', { docType: selectedDocType, version: selectedDocVersion || 'no version', url });
      const response = await axios.get(url);
      console.log('Fields loaded:', response.data.fields.length, 'fields');
      setFields(response.data.fields);
    } catch (error) {
      console.error('Error loading fields:', error);
      setFields([]);
    }
  };

  const loadPages = async () => {
    try {
      const params = new URLSearchParams();
      // Only add version if it exists (version is optional)
      if (selectedDocVersion) {
        params.append('version', selectedDocVersion);
      }
      const url = `${API_BASE}/documents/${selectedDocType}/pages${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setTotalPages(response.data.total_pages);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const loadRules = async () => {
    if (!selectedDocType) {
      setRules({
        required_fields: {},
        conditional_rules: [],
        unconditional_rules: [],
        custom_requirements: []
      });
      return;
    }
    try {
      const params = new URLSearchParams();
      // Only add version if it exists (version is optional)
      if (selectedDocVersion) {
        params.append('version', selectedDocVersion);
      }
      const url = `${API_BASE}/rules/${selectedDocType}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setRules(response.data);
    } catch (error) {
      console.error('Error loading rules:', error);
      setRules({
        required_fields: {},
        conditional_rules: [],
        unconditional_rules: [],
        custom_requirements: []
      });
    }
  };

  const handleFieldClick = (field) => {
    // If in conditional rule selection mode, handle it differently
    if (conditionalRuleMode && conditionalRuleMode.onFieldSelect) {
      conditionalRuleMode.onFieldSelect(field);
      return;
    }
    // If in unconditional rule selection mode, handle it differently
    if (unconditionalRuleMode && unconditionalRuleMode.onFieldSelect) {
      unconditionalRuleMode.onFieldSelect(field);
      return;
    }
    // Otherwise, normal field selection
    setSelectedField(field);
  };

  const handleRuleUpdate = () => {
    loadRules();
  };

  const handleDocumentSelectFromSearch = async (docType, docVersion) => {
    // Set the selected document type and version
    setSelectedDocType(docType);
    setSelectedDocVersion(docVersion);
    // Reload document types to include the newly downloaded one
    await loadDocumentTypes();
    // Show success message
    const versionText = docVersion ? ` (${docVersion})` : '';
    showToast(`Document "${docType}"${versionText} is now selected. You can now configure validation rules.`, 'success', 4000);
  };

  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Show API key prompt if not authenticated
  if (showApiKeyPrompt || !apiKey) {
    return <ApiKeyPrompt onSubmit={handleApiKeySubmit} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">DocuRule</h1>
          <DocumentSearch
            onDocumentSelect={handleDocumentSelectFromSearch}
            showToast={showToast}
            selectedDocType={selectedDocType}
            selectedDocVersion={selectedDocVersion}
          />
          <ConfigDownload
            docType={selectedDocType}
            docVersion={selectedDocVersion}
            showToast={showToast}
          />
        </div>
      </header>

      <div className="app-content">
        <div className="left-sidebar">
          <PageNavigation
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>

        <div className="main-content">
          <div className="pdf-container">
            {selectedDocType ? (
              <PDFViewer
                docType={selectedDocType}
                docVersion={selectedDocVersion}
                currentPage={currentPage}
                fields={fields.filter(f => f.page === currentPage)}
                selectedField={selectedField}
                onFieldClick={handleFieldClick}
                rules={rules}
                conditionalRuleMode={conditionalRuleMode}
                unconditionalRuleMode={unconditionalRuleMode}
              />
            ) : selectedDocType && !selectedDocVersion ? (
              <div className="no-document-selected">
                <div className="no-document-message">
                  <h2>Document Selected</h2>
                  <p>Document type: <strong>{selectedDocType}</strong></p>
                  <p>Please download a document from the search panel to load its version and data.</p>
                </div>
              </div>
            ) : (
              <div className="no-document-selected">
                <div className="no-document-message">
                  <h2>No Document Selected</h2>
                  <p>Use the search panel above to find and download a document.</p>
                  <p>Once downloaded, the document will be automatically selected and you can start configuring validation rules.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="right-sidebar">
          <FieldRulesPanel
            selectedField={selectedField}
            fields={fields}
            rules={rules}
            docType={selectedDocType}
            docVersion={selectedDocVersion}
            onRuleUpdate={handleRuleUpdate}
            showToast={showToast}
            onConditionalRuleModeChange={setConditionalRuleMode}
            onUnconditionalRuleModeChange={setUnconditionalRuleMode}
          />
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;

